import React from 'react';
import { Map, List } from 'immutable';
import { DropdownSelect, type SingleValue } from 'src/components/DropdownWrapper/DropdownWrapper';
import { ScrollBubblingMenuList } from 'src/components/DropdownWrapper/ScrollBubblingMenuList';
import { debounce } from 'lodash';
import { DateTimeInput } from 'src/components/DateTimeInput/DateTimeInput';
import { ParticipantsInputGroup } from 'src/components/ParticipantsInput/ParticipantsInputGroup';
import Form from 'src/components/Form/Form';
import Steps from 'src/components/Steps/Steps';
import { getCompetitionSelectStyles } from './CreateEventForm.style';

const CREATE_STEPS = ['sport', 'competition', 'type', 'participants', 'startTime'] as const;

const AVAILABLE_TIMEZONES = ['Local time'] as const;

const AVAILABLE_STEPS = {
    sport: true,
    competition: true,
    type: true,
    participants: true,
    startTime: true,
} as const;

const STEPS_VALIDATION = {
    sport: ['sport'],
    competition: ['competition'],
    type: ['template'],
    participants: ['participants'],
    startTime: ['timeSettings.startTime'],
} as const;

type StepType = (typeof CREATE_STEPS)[number];

interface ParticipantType {
    participant: string;
}

interface CompetitionType {
    get(key: 'id'): string | number;
    get(key: 'name'): string;
    get(key: 'label'): string;
    get(key: string): unknown;
    getIn?(keyPath: string[]): unknown;
}

interface SportType {
    get(key: 'id'): string;
    get(key: 'name'): string;
    get(key: 'eventTemplates'): List<EventTemplateType>;
    getIn(keyPath: string[]): unknown;
}

interface EventTemplateType {
    get(key: 'id'): string;
    get(key: 'name'): string;
    get(key: 'participants'): List<{ get(key: 'role'): string }>;
    get(key: string): unknown;
}

interface TimeSettingsType {
    startTime: string;
    timeZone?: string;
}

interface CreateEventFormDataType {
    sport: string;
    competition: string;
    competitionIdV2?: string;
    template: string;
    name?: string;
    participants: ParticipantType[];
    timeSettings: TimeSettingsType;
}

interface CompetitionOptionType {
    id: string | number;
    label: string;
    value: CompetitionType | undefined;
}

export interface CreateEventFormPropsType {
    className?: string;
    disabled?: boolean;
    onSubmit?: (data: CreateEventFormDataType) => void;
    onError?: (error: unknown) => void;
    onChange?: (field: string, value: unknown, data: CreateEventFormDataType) => void;
    onBlur?: (field: string, value: unknown, oldValue: unknown, error: string | null) => void;
    markValid?: boolean;
    success?: string;
    errors?: Map<string, string>;
    data?: Partial<CreateEventFormDataType>;
    sports: Map<string, SportType>;
    competitions: Map<string | number, CompetitionType>;
    competitionsCollection: Map<string, { state: string }>;
    participants: List<unknown>;
    onLoadCompetitionsRequest: (sportId: string, params: { sport: string; 'name.ngram': string }) => void;
    onStepFinish?: (currentStep: StepType, nextStep: StepType | null, data: CreateEventFormDataType) => void;
}

export class CreateEventForm extends Form {
    public static fieldsValidation = {
        sport: ['required'],
        template: ['required'],
        competition: ['required'],
        participants: [['required']],
        'timeSettings.startTime': ['required'],
    };

    public static defaultProps = {
        ...Form.defaultProps,
        sports: Map(),
        competitions: Map(),
        participants: List(),
    };

    private onSearchCompetitions: ReturnType<typeof debounce>;

    public constructor(props: CreateEventFormPropsType, context: unknown) {
        super(props, context);

        // @ts-expect-error - Form base class uses untyped state
        this.state.step = 'sport';

        this.onSearchCompetitions = debounce(
            (inputValue: string) => {
                const { onLoadCompetitionsRequest } = this.props;
                const sportId = this.state.data.sport;

                if (
                    onLoadCompetitionsRequest !== null &&
                    onLoadCompetitionsRequest !== undefined &&
                    inputValue !== '' &&
                    inputValue.length > 2
                ) {
                    onLoadCompetitionsRequest(sportId, {
                        sport: sportId,
                        ['name.ngram']: inputValue,
                    });
                }
            },
            500,
            { leading: false, trailing: true }
        );
    }

    private hasFinished(step: StepType): boolean {
        // @ts-expect-error - Form base class uses untyped state
        return CREATE_STEPS.indexOf(this.state.step) > CREATE_STEPS.indexOf(step);
    }

    private isLastStep(): boolean {
        return this.getNext() === null;
    }

    public _onChange(field: string, value: unknown, data: CreateEventFormDataType): void {
        const { competitions } = this.props;
        if (field === 'sport') {
            const hasCompetition = competitions.contains((c: CompetitionType): boolean => {
                const sportId = c.getIn?.(['sport', 'id']);
                const competitionId = c.get('id');
                return sportId === value && data.competition === String(competitionId);
            });

            if (hasCompetition === false) {
                // @ts-expect-error eslint-disable-next-line no-undefined
                data.competition = undefined;
                this.validate('competition', true);
            }
        }

        super._onChange(field, value, data);
    }

    private getNext(): StepType | null {
        // @ts-expect-error - Form base class uses untyped state
        const { data, step } = this.state;
        let currentIndex = CREATE_STEPS.indexOf(step) + 1;

        while (currentIndex < CREATE_STEPS.length) {
            const nextStep = CREATE_STEPS[currentIndex];
            if (nextStep === undefined) {
                break;
            }
            const condition = AVAILABLE_STEPS[nextStep];

            if (typeof condition === 'function' ? condition[data] === true : condition) {
                return nextStep;
            }

            currentIndex++;
        }

        return null;
    }

    private validateStep(step: StepType): boolean {
        let valid = true;

        STEPS_VALIDATION[step].forEach((field: string): void => {
            if (this.validate(field) !== null) {
                valid = false;
            }
        });

        return valid;
    }

    public validate(name: string, calledFromDependency?: boolean | string): string | null {
        const validation = this.getFieldsValidation()[name];
        const value = this.state.data[name];
        let message: string | null = null;

        if (validation !== undefined) {
            for (let i = 0; i < validation.length; i++) {
                let err: string | boolean;

                if (name === 'participants' && value !== undefined && Array.isArray(value)) {
                    if (value.length === 0) {
                        err = this.test(validation[i], null);
                    } else {
                        for (let idx = 0; idx < value.length; idx++) {
                            err = this.test(validation[i], value[idx].participant);
                            if (typeof err === 'string') {
                                break;
                            }
                        }
                    }
                } else {
                    err = this.test(validation[i], value);
                }

                if (
                    calledFromDependency !== undefined &&
                    calledFromDependency !== false &&
                    validation[i] === 'required'
                ) {
                    continue;
                }
                //@ts-expect-error
                if (typeof err === 'string') {
                    message = err;
                    break;
                }
            }

            const state = {
                // @ts-expect-error - Form base class uses untyped state
                invalid: this.state.invalid === undefined ? {} : this.state.invalid,
                errors: this.state.errors,
                // eslint-disable-next-line no-undefined
                success: undefined,
            };

            state.invalid[name] = typeof message === 'string';
            //@ts-expect-error
            state.errors[name] = message;

            // @ts-expect-error - Form base class uses untyped state
            this.state.invalid = state.invalid;
            this.state.errors = state.errors;

            this.setState(state);
        }

        const dependentUpon = this.getFieldsDependencies()[name];

        if (dependentUpon !== undefined) {
            dependentUpon.forEach((field: string): void => {
                if (calledFromDependency !== field) {
                    this.validate(field, name);
                }
            });
        }

        return message;
    }

    public onSubmit(e: React.FormEvent<HTMLFormElement>, ...args: unknown[]): void {
        // @ts-expect-error - Form base class uses untyped state
        const { step } = this.state;

        e.preventDefault();
        if (this.isLastStep()) {
            // @ts-expect-error  eslint-disable-next-line prefer-spread
            super.onSubmit.apply(this, [e, ...args]);
        } else {
            if (this.validateStep(step)) {
                if (step === 'competition') {
                    const { competitions } = this.props;
                    const competition = competitions.get(this.state.data.competition)?.toJS();
                    const data = {
                        ...this.state.data,
                        competitionIdV2: competition?.idV2,
                    };

                    this.setState({
                        ...this.state,
                        data,
                    });
                }
                this.next();
            }
        }
    }

    private isValidStep(step: string): step is StepType {
        return CREATE_STEPS.some((validStep: StepType): boolean => validStep === step);
    }

    private go(step: StepType | string, finished?: boolean): void {
        const { onStepFinish } = this.props;

        if (onStepFinish !== undefined && finished === true) {
            // @ts-expect-error - Form base class uses untyped state
            const currentStep = this.state.step;
            const targetStep = step === currentStep ? step : this.isValidStep(step) ? step : null;
            if (targetStep !== null) {
                onStepFinish(currentStep, targetStep, this.state.data);
            }
        }

        this.setState({
            //@ts-expect-error
            step: this.isValidStep(step) ? step : this.state.step,
        });
    }

    private next(): void {
        const nextStep = this.getNext();
        if (nextStep !== null) {
            this.go(nextStep, true);
        }
    }

    private getFilters(data: Map<string | number, CompetitionType>): CompetitionOptionType[] | null {
        if (data.isEmpty()) {
            return null;
        }

        const filteredData = data
            .map((x: CompetitionType | undefined): CompetitionOptionType => {
                if (x === undefined) {
                    return {
                        id: '',
                        label: '',
                        value: undefined,
                    };
                }

                const originalId = x.get('id');
                const numericId = parseInt(String(originalId), 10);
                // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                const label = x.get('name') || x.get('label') || '';

                return {
                    id: !isNaN(numericId) && numericId !== 0 ? numericId : originalId,
                    label,
                    value: x,
                };
            })
            .sortBy((x: CompetitionOptionType | undefined): string => x?.label ?? '')
            .toArray();

        return filteredData;
    }

    public renderForm(): JSX.Element {
        // @ts-expect-error - Form base class uses untyped state
        const { step, data } = this.state;
        const { sports, competitions, competitionsCollection } = this.props;

        const sport = this.hasFinished('sport') ? `Sport: ${sports.getIn([data.sport, 'name'])}` : 'Sport';

        const competition = this.hasFinished('competition')
            ? `Competition: ${competitions.getIn([parseInt(String(data.competition), 10), 'name'])}`
            : 'Competition';

        let type = 'Event type';
        if (this.hasFinished('type')) {
            const templates = sports.getIn([data.sport, 'eventTemplates']);
            const template = templates?.find((t: EventTemplateType): boolean => t.get('id') === data.template);
            if (template !== null && template !== undefined) {
                type = template.get('name');
            }
        }

        const templates = sports.getIn([data.sport, 'eventTemplates'], List<EventTemplateType>());
        const filteredCompetitions = this.getFilters(competitions) ?? [];
        const isLoadingCompetitions = competitionsCollection.getIn([data.sport, 'state']) === 'IN_PROGRESS';
        const steps: JSX.Element[] = [];
        steps.push(
            <div
                key='sport'
                title={sport}
            >
                {this.getField(
                    <select
                        //@ts-expect-error
                        label='Sport'
                        name={this.getFieldParameters('sport').name}
                        value={this.getFieldParameters('sport').value}
                        onChange={this.getFieldParameters('sport').onChange}
                        onFocus={this.getFieldParameters('sport').onFocus}
                        onBlur={this.getFieldParameters('sport').onBlur}
                    >
                        <option value={undefined} />

                        {sports
                            .valueSeq()
                            .map(
                                (sport: SportType): JSX.Element => (
                                    <option
                                        key={sport.get('id')}
                                        value={sport.get('id')}
                                    >
                                        {sport.get('name')}
                                    </option>
                                )
                            )
                            .toArray()}
                    </select>
                )}
                <button
                    type='submit'
                    className='btn btn-primary'
                >
                    Next
                </button>
            </div>
        );

        steps.push(
            <div
                key='competition'
                title={competition}
            >
                {this.getField(
                    <DropdownSelect<CompetitionOptionType, false>
                        name={this.getFieldParameters('competition').name}
                        value={filteredCompetitions.find(
                            (opt) => opt.id === this.getFieldParameters('competition').value
                        )}
                        onChange={(option: SingleValue<CompetitionOptionType>) => {
                            this.getFieldParameters('competition').onChange(option?.id ?? null);
                        }}
                        onFocus={this.getFieldParameters('competition').onFocus}
                        onBlur={this.getFieldParameters('competition').onBlur}
                        onInputChange={this.onSearchCompetitions}
                        options={filteredCompetitions}
                        isLoading={isLoadingCompetitions}
                        isClearable={true}
                        placeholder='Select competition'
                        noOptionsMessage={({ inputValue }) => {
                            if (inputValue === '' || inputValue.length < 3) {
                                return 'Type at least 3 characters to search';
                            }
                            if (isLoadingCompetitions) {
                                return 'Loading...';
                            }
                            return 'No competitions found';
                        }}
                        filterOption={() => true}
                        isSearchable={true}
                        getOptionValue={(option) => String(option.id)}
                        getOptionLabel={(option) => option.label}
                        customStyles={getCompetitionSelectStyles<CompetitionOptionType, false>()}
                        components={{
                            MenuList: ScrollBubblingMenuList,
                        }}
                    />
                )}

                <button
                    type='submit'
                    className='btn btn-primary'
                >
                    Next
                </button>
            </div>
        );

        steps.push(
            <div
                key='type'
                title={type}
            >
                {this.getField(
                    <select
                        //@ts-expect-error
                        label='Event type'
                        name={this.getFieldParameters('template').name}
                        value={this.getFieldParameters('template').value}
                        onChange={this.getFieldParameters('template').onChange}
                        onFocus={this.getFieldParameters('template').onFocus}
                        onBlur={this.getFieldParameters('template').onBlur}
                    >
                        <option />
                        {templates.map(
                            (t: EventTemplateType): JSX.Element => (
                                <option
                                    key={t.get('id')}
                                    value={t.get('id')}
                                >
                                    {t.get('name')}
                                </option>
                            )
                        )}
                    </select>
                )}

                <button
                    type='submit'
                    className='btn btn-primary'
                >
                    Next
                </button>
            </div>
        );

        const eventTemplate =
            this.props.sports
                .getIn([this.state.data.sport, 'eventTemplates'], List<EventTemplateType>())
                .find((t: EventTemplateType): boolean => t.get('id') === this.state.data.template) ?? Map();
        const rolesRaw = eventTemplate
            .get('participants', List())
            .map((p: { get(key: 'role'): string }): string => p.get('role'))
            .filter((r: string): boolean => Boolean(r))
            .toArray();
        const roles = rolesRaw.length === 0 ? false : rolesRaw;

        steps.push(
            <div
                key='participants'
                title='Participants'
            >
                {this.getField(
                    <ParticipantsInputGroup
                        sport={data.sport}
                        roles={roles}
                        // @ts-expect-error
                        name={this.getFieldParameters('participants').name}
                        value={this.getFieldParameters('participants').value}
                        onChange={this.getFieldParameters('participants').onChange}
                        onFocus={this.getFieldParameters('participants').onFocus}
                        onBlur={this.getFieldParameters('participants').onBlur}
                    />
                )}

                <button
                    type='submit'
                    className='btn btn-primary'
                >
                    Next
                </button>
            </div>
        );

        const valueName = 'timeSettings.startTime';
        const dateTimeParams = this.getFieldParameters(valueName);
        steps.push(
            <div
                key='startTime'
                className='create-event__step--start-time'
                title='Start time'
            >
                {this.getField(
                    <DateTimeInput
                        data-test='start-time-input'
                        className='datetime-input--top'
                        value={dateTimeParams.value ?? ''}
                        dateFormat='yyyy-MM-dd'
                        timeFormat='HH:mm:ss'
                        onChange={(event: string | null): void => dateTimeParams.onChange(event)}
                    />
                )}

                {this.getField(
                    <select
                        //@ts-expect-error
                        label='Time zone'
                        name={this.getFieldParameters('timeSettings.timeZone').name}
                        value={this.getFieldParameters('timeSettings.timeZone').value}
                        onChange={this.getFieldParameters('timeSettings.timeZone').onChange}
                        onFocus={this.getFieldParameters('timeSettings.timeZone').onFocus}
                        onBlur={this.getFieldParameters('timeSettings.timeZone').onBlur}
                    >
                        {AVAILABLE_TIMEZONES.map(
                            (tz: string): JSX.Element => (
                                <option
                                    key={tz}
                                    value='UTC'
                                >
                                    {tz}
                                </option>
                            )
                        )}
                    </select>
                )}

                <button
                    type='submit'
                    className='btn btn-primary'
                    data-test='add-event-button'
                >
                    Add event
                </button>
            </div>
        );

        return (
            <Steps
                active={step}
                allowBack={true}
                onChange={(changedStep: string): void => {
                    this.go(changedStep, false);
                }}
            >
                {steps}
            </Steps>
        );
    }
}
